import { z } from "zod";

export const PlaceSchema = z.object({
  jurisdictions: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      nameAr: z.string(),
    }),
  ),
  racecourses: z.array(
    z.object({
      id: z.string(),
      jurisdictionId: z.string(),
      code: z.string(),
      name: z.string(),
      nameAr: z.string(),
    }),
  ),
  filters: z.object({
    minimumDistanceMeters: z.number().int().nullable(),
    maximumDistanceMeters: z.number().int().nullable(),
    grades: z.array(z.string()),
    categories: z.array(z.string()),
    ageRestrictions: z.array(z.string()),
    sexRestrictions: z.array(z.string()),
    benchmarkRatings: z.array(z.number().int()),
    ratedMinimums: z.array(z.number().int()),
    seasons: z.array(z.string()),
  }),
});

export const RaceSchema = z.object({
  id: z.string(),
  raceDate: z.string(),
  track: z.string().nullable(),
  weekday: z
    .enum([
      "MONDAY",
      "TUESDAY",
      "WEDNESDAY",
      "THURSDAY",
      "FRIDAY",
      "SATURDAY",
      "SUNDAY",
    ])
    .nullable(),
  meetingNumber: z.number().int().nullable(),
  raceNumber: z.number().int().nullable(),
  raceName: z.string().nullable(),
  grade: z.string().nullable(),
  surface: z.enum(["DIRT", "TURF", "ALL_WEATHER"]).nullable(),
  distanceMeters: z.number().nullable(),
  breed: z.enum(["OPEN", "THOROUGHBRED", "PUREBRED_ARABIAN", "OTHER"]),
  category: z.string().nullable(),
  handicapLow: z.number().int().nullable(),
  handicapHigh: z.number().int().nullable(),
  benchmarkRating: z.number().int().nullable(),
  ageRestriction: z.string().nullable(),
  minAge: z.number().int().nullable(),
  sexRestriction: z.string().nullable(),
  uaeBredOnly: z.boolean().nullable(),
  ratedMinimum: z.number().int().nullable(),
  privateOwnersOnly: z.boolean().nullable(),
  invitationOnly: z.boolean().nullable(),
  purseMinor: z.string().nullable(),
  currency: z.string().nullable(),
  purseAedMinor: z.string().nullable(),
  entryFee: z.string().nullable(),
  declarationFee: z.string().nullable(),
  baseWeight: z.string().nullable(),
  penalties: z.string().nullable(),
  allowances: z.string().nullable(),
  eligibility: z.string().nullable(),
  notes: z.string().nullable(),
  ratingQualificationPublished: z.string().date().nullable(),
  weightsPublished: z.string().date().nullable(),
  entriesClose: z.string().datetime({ offset: true }).nullable(),
  declarationsClose: z.string().datetime({ offset: true }).nullable(),
  jockeyDeclarationsClose: z.string().datetime({ offset: true }).nullable(),
  season: z.string(),
  saved: z.boolean(),
  racecourse: z.object({
    code: z.string(),
    name: z.string(),
    nameAr: z.string(),
  }),
});

export const ResultSchema = z.object({
  races: z.array(RaceSchema),
  nextCursor: z.string().nullable(),
  totalCount: z.number().int().nonnegative(),
});

export type PlannerLocale = "en" | "ar";
export type PlannerPlaces = z.infer<typeof PlaceSchema>;
export type PlannerRace = z.infer<typeof RaceSchema>;
export type PlannerSort = "NEWEST" | "OLDEST";
export type PlannerFilters = {
  readonly jurisdiction: string;
  readonly racecourse: string;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly surface: string;
  readonly distanceFrom: string;
  readonly distanceTo: string;
  readonly grade: string;
  readonly category: string;
  readonly ageRestriction: string;
  readonly sexRestriction: string;
  readonly handicapRating: string;
  readonly benchmarkRating: string;
  readonly ratedMinimum: string;
  readonly uaeBredOnly: string;
  readonly privateOwnersOnly: string;
  readonly invitationOnly: string;
  readonly breed: string;
  readonly season: string;
};

export const EMPTY_PLANNER_FILTERS: PlannerFilters = {
  jurisdiction: "",
  racecourse: "",
  dateFrom: "",
  dateTo: "",
  surface: "",
  distanceFrom: "",
  distanceTo: "",
  grade: "",
  category: "",
  ageRestriction: "",
  sexRestriction: "",
  handicapRating: "",
  benchmarkRating: "",
  ratedMinimum: "",
  uaeBredOnly: "",
  privateOwnersOnly: "",
  invitationOnly: "",
  breed: "",
  season: "",
};
